import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { FaChevronDown, FaChevronUp, FaExclamationTriangle } from 'react-icons/fa';
import '../styles/BillingHistory.scss';

const BillingHistory: React.FC = () => {
    const { months, status } = useSelector((state: RootState) => state.billingHistory);
    const [expandedMonths, setExpandedMonths] = useState<Record<string, boolean>>({});

    const formatCurrency = (val: any): string => {
        const num = typeof val === 'number' ? val : parseFloat(val);
        return isNaN(num) ? '0.0000' : num.toFixed(4);
    };

    const toggleMonth = (month: string) => {
        setExpandedMonths(prev => ({
            ...prev,
            [month]: !prev[month]
        }));
    };

    if (status === 'failed' && (!months || months.length === 0)) {
        return (
            <div className="history-error">
                <FaExclamationTriangle />
                <p>Could not connect to the billing service. Please check if the server is running.</p>
            </div>
        );
    }

    if (status === 'loading' && (!months || months.length === 0)) {
        return <div className="history-loading">Gathering history records...</div>;
    }

    // 2. Filter out any "junk" records that might have sneaked into state
    const validMonths = (months || []).filter(m => m && m.month && !isNaN(new Date(m.month).getTime()));

    if (validMonths.length === 0) {
        return <div className="history-empty">No past usage records found.</div>;
    }

    return (
        <div className="billing-history-list">
            {validMonths.map((monthData) => {
                const periodKey = `${monthData.source || 'legacy'}:${monthData.month}`;
                const isExpanded = expandedMonths[periodKey];
                const dateLabel = new Date(monthData.month).toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric'
                });
                const netPeriod = monthData.source === 'plan_ledger';
                const periodLabel = netPeriod ? `${new Date(monthData.month).toLocaleDateString('en-GB', {timeZone:'UTC'})} - ${new Date(monthData.period_end).toLocaleDateString('en-GB', {timeZone:'UTC'})} (UTC)` : dateLabel;

                return (
                    <div key={periodKey} className={`month-accordion ${isExpanded ? 'open' : ''}`}>
                        <div className="accordion-header" onClick={() => toggleMonth(periodKey)}>
                            <div className="month-info">
                                <span className="date-label">{periodLabel}</span>
                                <span className="month-total">
                                    £{formatCurrency(monthData.monthly_total)}
                                </span>
                                {netPeriod && <span>Additional usage after credits</span>}
                                {monthData.source === 'legacy_gross' && <span>Legacy gross usage before credits</span>}
                            </div>
                            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </div>

                        {isExpanded && (() => {
                            const byService = Array.isArray(monthData.by_service) ? monthData.by_service : [];
                            const nonEgressRows = byService.filter((svc: any) => svc.service !== 'egress');
                            const egressEntries = byService.filter((svc: any) => svc.service === 'egress');
                            const totalEgressGB = egressEntries.reduce((sum: number, svc: any) =>
                                sum + (svc.metadata?.chargeable_gb ?? 0), 0);
                            // For egress, actual cost is stored in current_hourly_rate, not actual_cost
                            const totalEgressCost = egressEntries.reduce((sum: number, svc: any) =>
                                sum + (parseFloat(svc.current_hourly_rate) || parseFloat(svc.actual_cost) || 0), 0);
                            return (
                                <div className="accordion-content">
                                    {netPeriod && <p>Credits applied: £{formatCurrency(monthData.credits_applied)}. Additional usage shown below is already after credits; do not subtract them again.{monthData.commitment_gbp > 0
                                        ? ` Your monthly commitment of £${formatCurrency(monthData.commitment_gbp)} is charged separately and comes back as usage credits for its billing period.` : ''} Taxes are excluded; see your invoice for the amount due.</p>}
                                    {monthData.source === 'legacy_gross' && <p>These records retain your pre-migration billing history. They show gross usage, not the amount collected after legacy credits and taxes. See your original invoice for the amount due.</p>}
                                    {egressEntries.length > 0 && (
                                        <div style={{ marginBottom: "12px", padding: "12px 15px", backgroundColor: "var(--surface-alt)", borderRadius: "8px", border: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <div>
                                                <span style={{ fontWeight: "bold", color: "var(--text-primary)" }}>Egress</span>
                                                <span style={{ color: "var(--text-secondary)", fontSize: "0.9em", marginLeft: "8px" }}>
                                                    {totalEgressGB.toFixed(4)} GB chargeable
                                                </span>
                                            </div>
                                            <span style={{ fontWeight: "bold" }}>£{totalEgressCost.toFixed(4)}</span>
                                        </div>
                                    )}
                                    <table className="history-table">
                                        <thead>
                                        <tr>
                                            <th>Service / Resource</th>
                                            <th>{netPeriod ? 'Usage from' : 'Started At'}</th>
                                            <th>{netPeriod ? 'Usage until' : 'Ended At'}</th>
                                            <th>Usage</th>
                                            <th>{netPeriod ? 'After credits' : 'Cost'}</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {nonEgressRows.length > 0 ? (
                                            nonEgressRows.map((svc: any, idx: number) => (
                                                <tr key={`${svc.os_resource_id || 'no-id'}-${idx}`}>
                                                    <td>
                                                        <div className="service-type">{svc.service.replaceAll('_', ' ')}</div>
                                                        <div className="resource-name">
                                                            {svc.metadata?.name || svc.os_resource_id || 'System Resource'}
                                                            {svc.service === 'instance' && svc.metadata?.flavor && ` (${svc.metadata.flavor})`}
                                                        </div>
                                                    </td>
                                                    <td>{svc.recorded_at ? new Date(svc.recorded_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) : "-"}</td>
                                                    <td>{svc.ended_at ? new Date(svc.ended_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) : "-"}</td>
                                                    <td>{(svc.hours_active || 0) + " hrs"}</td>
                                                    <td>£{formatCurrency(svc.actual_cost)}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={5} style={{ textAlign: 'center', padding: '1rem' }}>
                                                    No service data for this month.
                                                </td>
                                            </tr>
                                        )}
                                        </tbody>
                                    </table>
                                </div>
                            );
                        })()}
                    </div>
                );
            })}
        </div>
    );
};

export default BillingHistory;
